import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, CreditCard, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NfcRequest() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    dni: '',
    direccion: '',
    codigo_postal: '',
    comentarios: '',
    consentimiento_rgpd: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre || formData.nombre.length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.telefono || formData.telefono.length < 9) {
      newErrors.telefono = 'El teléfono debe tener al menos 9 dígitos';
    }

    if (!formData.dni || formData.dni.length < 8) {
      newErrors.dni = 'El DNI/NIE debe tener al menos 8 caracteres';
    }

    if (!formData.direccion || formData.direccion.length < 10) {
      newErrors.direccion = 'La dirección debe tener al menos 10 caracteres';
    }

    if (!formData.consentimiento_rgpd) {
      newErrors.consentimiento_rgpd = 'Debes aceptar el tratamiento de datos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Limpiar error del campo al escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrige los errores en el formulario');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/nfc-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al enviar la solicitud');
      }

      const data = await response.json();
      setRequestId(data.id);
      setIsSuccess(true);
      toast.success('¡Solicitud enviada correctamente!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al enviar la solicitud. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <Helmet>
          <title>Solicitud Enviada | Cuidemos Los Alcázares®</title>
          <meta name="description" content="Tu solicitud de tarjeta NFC ha sido enviada correctamente" />
        </Helmet>

        <section className="py-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[rgba(31,138,112,0.12)] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-[#1F8A70]" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#0A2540] mb-4">
              ¡Solicitud recibida!
            </h1>
            <p className="text-[#6F8AA8] text-lg mb-6">
              Tu solicitud de tarjeta NFC ha sido enviada correctamente.
            </p>
            <div className="bg-[#F5F9FC] border border-[#CFE3F5] rounded-2xl p-6 mb-8">
              <p className="text-sm text-[#6F8AA8] mb-2">Número de referencia</p>
              <p className="font-mono text-lg text-[#0A2540] font-semibold break-all">
                {requestId}
              </p>
            </div>
            <p className="text-sm text-[#6F8AA8] mb-8">
              Hemos enviado una notificación a nuestro equipo. Nos pondremos en contacto contigo en breve
              para gestionar tu tarjeta NFC.
            </p>
            <button
              onClick={() => navigate('/ciudadania')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors"
            >
              <ArrowLeft size={18} />
              Volver a Ciudadanía
            </button>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Solicitar Tarjeta NFC | Cuidemos Los Alcázares®</title>
        <meta
          name="description"
          content="Solicita tu tarjeta NFC para acceder a los contenedores de fracción orgánica en Los Alcázares"
        />
        <meta property="og:title" content="Solicitar Tarjeta NFC | Cuidemos Los Alcázares®" />
        <meta
          property="og:description"
          content="Solicita tu tarjeta NFC para acceder a los contenedores de fracción orgánica"
        />
      </Helmet>

      {/* Hero */}
      <section className="pt-24 pb-12 px-6 bg-gradient-to-br from-[#FAFCFE] via-white to-[#F5F9FC]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(30,90,168,0.12)] flex items-center justify-center mx-auto mb-6">
            <CreditCard size={30} className="text-[#1E5AA8]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0A2540] mb-4">
            Solicita tu tarjeta NFC
          </h1>
          <p className="text-[#6F8AA8] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            La tarjeta NFC te permite abrir los contenedores de fracción orgánica de tu zona.
            Rellena el formulario y nos pondremos en contacto contigo.
          </p>
        </div>
      </section>

      {/* Formulario */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-semibold text-[#0A2540] mb-2">
                Nombre completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${errors.nombre ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all`}
                placeholder="Juan García López"
                disabled={isSubmitting}
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.nombre}
                </p>
              )}
            </div>

            {/* DNI */}
            <div>
              <label htmlFor="dni" className="block text-sm font-semibold text-[#0A2540] mb-2">
                DNI/NIE <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="dni"
                name="dni"
                value={formData.dni}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${errors.dni ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all`}
                placeholder="12345678A"
                disabled={isSubmitting}
              />
              {errors.dni && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.dni}
                </p>
              )}
            </div>

            {/* Email y Teléfono */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all`}
                  placeholder="correo@ejemplo.com"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="telefono" className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Teléfono <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.telefono ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all`}
                  placeholder="+34 612 345 678"
                  disabled={isSubmitting}
                />
                {errors.telefono && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.telefono}
                  </p>
                )}
              </div>
            </div>

            {/* Dirección */}
            <div>
              <label htmlFor="direccion" className="block text-sm font-semibold text-[#0A2540] mb-2">
                Dirección completa <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${errors.direccion ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all`}
                placeholder="Calle Mayor 123, Los Alcázares"
                disabled={isSubmitting}
              />
              {errors.direccion && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.direccion}
                </p>
              )}
            </div>

            {/* Código Postal */}
            <div>
              <label htmlFor="codigo_postal" className="block text-sm font-semibold text-[#0A2540] mb-2">
                Código Postal
              </label>
              <input
                type="text"
                id="codigo_postal"
                name="codigo_postal"
                value={formData.codigo_postal}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all"
                placeholder="30710"
                disabled={isSubmitting}
              />
            </div>

            {/* Comentarios */}
            <div>
              <label htmlFor="comentarios" className="block text-sm font-semibold text-[#0A2540] mb-2">
                Comentarios adicionales
              </label>
              <textarea
                id="comentarios"
                name="comentarios"
                value={formData.comentarios}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] focus:border-transparent transition-all resize-none"
                placeholder="Información adicional que consideres relevante..."
                disabled={isSubmitting}
              />
            </div>

            {/* Consentimiento RGPD */}
            <div className="bg-[#F5F9FC] border border-[#CFE3F5] rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="consentimiento_rgpd"
                  name="consentimiento_rgpd"
                  checked={formData.consentimiento_rgpd}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 rounded border-[#CFE3F5] text-[#1E5AA8] focus:ring-[#1E5AA8] cursor-pointer"
                  disabled={isSubmitting}
                />
                <label htmlFor="consentimiento_rgpd" className="text-sm text-[#6F8AA8] cursor-pointer">
                  <span className="text-red-500 font-semibold">*</span> Acepto el tratamiento de mis datos personales
                  conforme al RGPD y LOPDGDD para la gestión de mi solicitud de tarjeta NFC.
                  Los datos serán tratados por el Ayuntamiento de Los Alcázares y Valoriza
                  Servicios Medioambientales con la finalidad de tramitar mi solicitud.
                </label>
              </div>
              {errors.consentimiento_rgpd && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1 ml-8">
                  <AlertCircle size={14} /> {errors.consentimiento_rgpd}
                </p>
              )}
            </div>

            {/* Botones */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/ciudadania')}
                className="flex-1 px-8 py-4 rounded-full border-2 border-[#CFE3F5] text-[#0A2540] text-sm font-medium hover:bg-[#FAFCFE] transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar solicitud'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}